import type { FlowGeneratorSession } from "@/types/flow.ts";
import { create } from "zustand/react";
import type { TutorId } from "@/types/tutor.ts";
import { useSignalStore } from "@/stores/useSignalrStore.ts";
import { SignalInvoke } from "@/types/signalr-functions.ts";
import { toastAndError } from "@/helpers/toast.ts";
import type { SessionAlert } from "@/types/message.ts";

export type ActiveSessionStore = {
  activeSessions: Map<TutorId, Array<Partial<FlowGeneratorSession>>>;
  helpRequestedSessions: Array<Partial<FlowGeneratorSession>>;
  flaggedSessions: Array<SessionAlert>;

  AddNewSession: (tutorId: TutorId, flow: Partial<FlowGeneratorSession>) => void;
  RemoveSession: (flow: { flowGeneratorSessionId?: number | null; id?: number | null }) => void;
  AddNewFlaggedSession: (flow: SessionAlert) => void;
  RemoveFlaggedSession: (flowSes: { flowGeneratorSessionId?: number | null }) => void;

  AddHelpRequestedSession: (flow: Partial<FlowGeneratorSession>) => void;
  RemoveHelpRequestedSession: (flow: { flowGeneratorSessionId?: number | null }) => void;
  ClearSessions: () => void;
};

export const useActiveSessionStore = create<ActiveSessionStore>((set) => ({
  activeSessions: new Map<number, Array<Partial<FlowGeneratorSession>>>(),
  helpRequestedSessions: [],
  flaggedSessions: [],

  AddNewSession: (tutorId, flow) =>
    set((state) => {
      const activeSessions = new Map(state.activeSessions);

      for (const [id, sessions] of activeSessions) {
        const filtered = sessions.filter(
          (session) =>
            !(
              (flow.flowGeneratorSessionId != null &&
                session.flowGeneratorSessionId === flow.flowGeneratorSessionId) ||
              (flow.id != null && session.id === flow.id)
            ),
        );

        activeSessions.set(id, filtered);
      }

      const existing = activeSessions.get(tutorId) || [];
      activeSessions.set(tutorId, [...existing, flow]);

      const { signalr } = useSignalStore.getState();

      if (signalr)
        signalr
          .Invoke(SignalInvoke.JoinAiTutorResponseGroup, flow.flowGeneratorSessionId?.toString())
          .catch((e) =>
            toastAndError(
              "Unable to invoke JoinAiTutorGroup " + flow.flowGeneratorSessionId?.toString(),
              e,
            ),
          );

      return { activeSessions };
    }),
  RemoveSession: (flow) =>
    set((state) => {
      const activeSessions = new Map(state.activeSessions);

      for (const [id, sessions] of activeSessions) {
        const filtered = sessions.filter(
          (session) =>
            !(
              (flow.flowGeneratorSessionId != null &&
                session.flowGeneratorSessionId === flow.flowGeneratorSessionId) ||
              (flow.id != null && session.id === flow.id)
            ),
        );
        activeSessions.set(id, filtered);
      }

      return { activeSessions };
    }),

  AddNewFlaggedSession: (alert) =>
    set((state) => {
      const existing = state.flaggedSessions.find(
        (session) => session.FlowGeneratorSessionId === alert.FlowGeneratorSessionId,
      );
      if (existing) return state;

      const newSessions = [...state.flaggedSessions, alert];
      return { flaggedSessions: newSessions };
    }),
  RemoveFlaggedSession: (flow) =>
    set((state) => {
      const newSessions = state.flaggedSessions.filter(
        (session) => session.FlowGeneratorSessionId !== flow.flowGeneratorSessionId,
      );
      return { flaggedSessions: newSessions };
    }),
  AddHelpRequestedSession: (flow) =>
    set((state) => {
      const existing = state.helpRequestedSessions.find(
        (session) => session.flowGeneratorSessionId === flow.flowGeneratorSessionId,
      );
      if (existing) return state;

      const newSessions = [...state.helpRequestedSessions, flow];
      return { helpRequestedSessions: newSessions };
    }),
  RemoveHelpRequestedSession: (flow) =>
    set((state) => {
      const newSessions = state.helpRequestedSessions.filter(
        (session) => session.flowGeneratorSessionId !== flow.flowGeneratorSessionId,
      );
      return { helpRequestedSessions: newSessions };
    }),
  ClearSessions: () =>
    set(() => {
      return { activeSessions: new Map(), helpRequestedSessions: [] };
    }),
}));
